/**
 * Data Validation Utilities
 * 
 * Validation functions for form data before saving to localStorage
 */

import { JobPostFormData } from '../schemas/jobPostValidation';

/**
 * Validate form data structure before saving
 * Returns true if valid, false otherwise
 */
export const validateFormData = (data: any): data is JobPostFormData => {
  // Basic structure check
  if (!data || typeof data !== 'object') {
    return false;
  }
  
  // Check required top-level properties
  if (typeof data.currentStep !== 'number') {
    return false;
  }
  
  if (!['new', 'edit', 'clone'].includes(data.mode)) {
    return false;
  }
  
  // Check step data exists (can be empty objects/arrays)
  if (!data.step0 || typeof data.step0 !== 'object') {
    return false;
  }
  
  if (!data.step1 || typeof data.step1 !== 'object') {
    return false;
  }
  
  if (!data.step2 || typeof data.step2 !== 'object') {
    return false;
  }
  
  if (!data.step3 || typeof data.step3 !== 'object') {
    return false;
  }
  
  if (!data.step4 || typeof data.step4 !== 'object') {
    return false;
  }
  
  if (!data.step5 || typeof data.step5 !== 'object') {
    return false;
  }
  
  if (!data.step6 || typeof data.step6 !== 'object') {
    return false;
  }
  
  return true;
};

/**
 * Sanitize form data before saving
 * Removes any invalid properties and ensures data structure is correct
 */
export const sanitizeFormData = (data: any): JobPostFormData | null => {
  try {
    // Deep clone to avoid mutating original
    const sanitized = JSON.parse(JSON.stringify(data));
    
    // Ensure all required steps exist
    if (!sanitized.step0) sanitized.step0 = {};
    if (!sanitized.step1) sanitized.step1 = {};
    if (!sanitized.step2) sanitized.step2 = {};
    if (!sanitized.step3) sanitized.step3 = {};
    if (!sanitized.step4) sanitized.step4 = {};
    if (!sanitized.step5) sanitized.step5 = {};
    if (!sanitized.step6) sanitized.step6 = {};
    
    // Ensure mode is valid
    if (!['new', 'edit', 'clone'].includes(sanitized.mode)) {
      sanitized.mode = 'new';
    }
    
    // Ensure currentStep is a number
    if (typeof sanitized.currentStep !== 'number') {
      sanitized.currentStep = 0;
    }
    
    // Validate the sanitized data
    if (validateFormData(sanitized)) {
      return sanitized;
    }
    
    return null;
  } catch (error) {
    console.error('Error sanitizing form data:', error);
    return null;
  }
};

/**
 * Check if data can be safely serialized to JSON
 */
export const canSerializeData = (data: any): boolean => {
  try {
    JSON.stringify(data);
    return true;
  } catch (error) {
    return false;
  }
};






